import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { FileText, Building2, Droplets, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MiCuenta = () => {
  const { user } = useAuth();
  const [lots, setLots] = useState([]);
  const [selectedLot, setSelectedLot] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [accountStatement, setAccountStatement] = useState(null);
  const [waterReadings, setWaterReadings] = useState([]);
  const [waterMeter, setWaterMeter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('estado-cuenta');

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  useEffect(() => {
    fetchUserLots();
  }, [user]);

  useEffect(() => {
    if (selectedLot && selectedYear) {
      fetchAccountStatement();
      fetchWaterData();
    }
  }, [selectedLot, selectedYear]);

  const fetchUserLots = async () => {
    try {
      // Get user's assigned lots from lot_users
      const response = await axios.get(`${API}/lot-users`);
      const userAssignments = response.data.filter(lu => lu.user_id === user.id);
      const lotIds = userAssignments.map(lu => lu.lot_id);
      
      if (lotIds.length > 0) {
        // Get all lots and filter by assigned ones
        const lotsRes = await axios.get(`${API}/lots`);
        const userLots = lotsRes.data.filter(l => lotIds.includes(l.id));
        setLots(userLots);
        
        if (userLots.length > 0) {
          setSelectedLot(userLots[0].id);
        }
      }
    } catch (error) {
      console.error('Error fetching user lots:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAccountStatement = async () => {
    try {
      const response = await axios.get(`${API}/reports/account-statement?lot_id=${selectedLot}&año=${selectedYear}`);
      setAccountStatement(response.data);
    } catch (error) {
      console.error('Error fetching account statement:', error);
      setAccountStatement(null);
    }
  };

  const fetchWaterData = async () => {
    if (!selectedLot) return;
    
    try {
      // First get the lot to find park_id
      const lotData = lots.find(l => l.id === selectedLot);
      if (!lotData) return;

      // Get water meter for this lot
      const metersRes = await axios.get(`${API}/water/meters?park_id=${lotData.park_id}`);
      const lotMeter = metersRes.data.find(m => m.lot_id === selectedLot);
      setWaterMeter(lotMeter);

      if (lotMeter) {
        // Get readings for this meter
        const readingsRes = await axios.get(`${API}/water/readings?park_id=${lotData.park_id}&meter_id=${lotMeter.id}`);
        // Filter by year
        const yearReadings = readingsRes.data.filter(r => {
          const readingYear = new Date(r.fecha_lectura).getFullYear();
          return readingYear === selectedYear;
        });
        // Sort by date descending
        yearReadings.sort((a, b) => new Date(b.fecha_lectura) - new Date(a.fecha_lectura));
        setWaterReadings(yearReadings);
      } else {
        setWaterReadings([]);
      }
    } catch (error) {
      console.error('Error fetching water data:', error);
      setWaterMeter(null);
      setWaterReadings([]);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(value || 0);
  };

  if (loading) {
    return (
      <div className="animate-fadeIn">
        <div className="page-header">
          <h1>Mi Estado de Cuenta</h1>
          <p>Cargando...</p>
        </div>
      </div>
    );
  }

  if (lots.length === 0) {
    return (
      <div className="animate-fadeIn">
        <div className="page-header">
          <h1>Mi Estado de Cuenta</h1>
          <p>Bienvenido, {user?.nombre}</p>
        </div>
        <div className="empty-state py-24 card-industrial">
          <div className="empty-state-icon bg-amber-100">
            <Building2 size={32} className="text-amber-600" />
          </div>
          <h3>Sin lotes asignados</h3>
          <p>Aún no tienes lotes asignados a tu cuenta. Contacta al administrador.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fadeIn">
      <div className="page-header">
        <h1 data-testid="mi-cuenta-title">Mi Estado de Cuenta</h1>
        <p>Bienvenido, {user?.nombre}</p>
      </div>

      {/* Selectors */}
      <div className="card-industrial mb-8">
        <div className="flex items-center gap-6">
          <div>
            <label className="label-overline block mb-2">Lote</label>
            <Select value={selectedLot} onValueChange={setSelectedLot}>
              <SelectTrigger className="w-48 input-industrial" data-testid="my-lot-select">
                <SelectValue placeholder="Seleccionar lote..." />
              </SelectTrigger>
              <SelectContent>
                {lots.map(lot => (
                  <SelectItem key={lot.id} value={lot.id}>{lot.numero_lote}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="label-overline block mb-2">Año</label>
            <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
              <SelectTrigger className="w-32 input-industrial" data-testid="my-year-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map(year => (
                  <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Tabs for Account Statement and Water Consumption */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="estado-cuenta" className="flex items-center gap-2" data-testid="tab-estado-cuenta">
            <FileText size={16} />
            Estado de Cuenta
          </TabsTrigger>
          <TabsTrigger value="consumo-agua" className="flex items-center gap-2" data-testid="tab-consumo-agua">
            <Droplets size={16} />
            Consumo de Agua
          </TabsTrigger>
        </TabsList>

        {/* Estado de Cuenta Tab */}
        <TabsContent value="estado-cuenta">
          {accountStatement ? (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8 mt-6">
                <div className="card-industrial">
                  <span className="label-overline">Lote</span>
                  <p className="text-2xl font-black tracking-tight mt-2">{accountStatement.lot.numero_lote}</p>
                  <p className="text-sm text-slate-500 mt-1">
                    {accountStatement.lot.metros_cuadrados?.toLocaleString()} m² | {accountStatement.lot.indiviso}% indiviso
                  </p>
                </div>
                <div className="card-industrial">
                  <span className="label-overline">Total Cargos</span>
                  <p className="text-2xl font-black tracking-tight mt-2 tabular-nums">
                    {formatCurrency(accountStatement.summary.total_cargos_total || accountStatement.summary.total_cargos)}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Base: {formatCurrency(accountStatement.summary.total_cargos_base || accountStatement.summary.total_cargos)} + IVA: {formatCurrency(accountStatement.summary.total_cargos_iva || 0)}
                  </p>
                </div>
                <div className="card-industrial">
                  <span className="label-overline">Total Pagos</span>
                  <p className="text-2xl font-black tracking-tight mt-2 tabular-nums text-emerald-600">
                    {formatCurrency(accountStatement.summary.total_abonos_total || accountStatement.summary.total_abonos)}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Base: {formatCurrency(accountStatement.summary.total_abonos_base || accountStatement.summary.total_abonos)} + IVA: {formatCurrency(accountStatement.summary.total_abonos_iva || 0)}
                  </p>
                </div>
                <div className="card-industrial">
                  <span className="label-overline">Saldo Actual</span>
                  <p className={`text-2xl font-black tracking-tight mt-2 tabular-nums ${(accountStatement.summary.saldo_total || accountStatement.summary.saldo_actual) > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                    {formatCurrency(accountStatement.summary.saldo_total || accountStatement.summary.saldo_actual)}
                  </p>
                  {(accountStatement.summary.saldo_total || accountStatement.summary.saldo_actual) > 0 && (
                    <>
                      <p className="text-xs text-slate-500 mt-1">
                        Base: {formatCurrency(accountStatement.summary.saldo_base || 0)} + IVA: {formatCurrency(accountStatement.summary.saldo_iva || 0)}
                      </p>
                      <p className="text-sm text-red-500 mt-1">Pendiente de pago</p>
                    </>
                  )}
                </div>
              </div>

              {/* Movements Table */}
              <div className="card-industrial overflow-hidden p-0">
                <div className="px-6 py-4 border-b border-slate-200">
                  <h3 className="font-bold text-lg">Movimientos del Año {selectedYear}</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="table-blueprint" data-testid="my-account-table">
                    <thead>
                      <tr>
                        <th>Fecha</th>
                        <th>Concepto</th>
                        <th className="text-right">Base</th>
                        <th className="text-right">IVA (16%)</th>
                        <th className="text-right">Total</th>
                        <th className="text-right">Saldo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {accountStatement.movements.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="text-center py-8 text-slate-500">
                            No hay movimientos registrados
                          </td>
                        </tr>
                      ) : (
                        accountStatement.movements.map((mov, index) => {
                          const isCharge = (mov.cargo_total || mov.cargo) > 0;
                          const baseAmount = isCharge ? (mov.cargo_base || mov.cargo) : (mov.abono_base || mov.abono);
                          const ivaAmount = isCharge ? (mov.cargo_iva || 0) : (mov.abono_iva || 0);
                          const totalAmount = isCharge ? (mov.cargo_total || mov.cargo) : (mov.abono_total || mov.abono);
                          const colorClass = isCharge ? 'text-red-600' : 'text-emerald-600';
                          const prefix = isCharge ? '' : '-';
                          const saldo = mov.saldo_total !== undefined ? mov.saldo_total : mov.saldo;
                          
                          return (
                            <tr key={`${mov.fecha}-${mov.concepto}-${index}`}>
                              <td className="font-mono text-sm">{mov.fecha}</td>
                              <td>{mov.concepto}</td>
                              <td className={`text-right tabular-nums ${colorClass}`}>
                                {baseAmount > 0 ? `${prefix}${formatCurrency(baseAmount)}` : '-'}
                              </td>
                              <td className={`text-right tabular-nums ${colorClass}`}>
                                {ivaAmount > 0 ? `${prefix}${formatCurrency(ivaAmount)}` : '-'}
                              </td>
                              <td className={`text-right tabular-nums font-medium ${colorClass}`}>
                                {totalAmount > 0 ? `${prefix}${formatCurrency(totalAmount)}` : '-'}
                              </td>
                              <td className={`text-right tabular-nums font-medium ${saldo > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                                {formatCurrency(saldo)}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                    {accountStatement.movements.length > 0 && (
                      <tfoot>
                        <tr className="bg-slate-50 font-bold">
                          <td colSpan={2}>Saldo Final</td>
                          <td className="text-right tabular-nums">
                            {formatCurrency((accountStatement.summary.total_cargos_base || accountStatement.summary.total_cargos) - (accountStatement.summary.total_abonos_base || accountStatement.summary.total_abonos))}
                          </td>
                          <td className="text-right tabular-nums">
                            {formatCurrency((accountStatement.summary.total_cargos_iva || 0) - (accountStatement.summary.total_abonos_iva || 0))}
                          </td>
                          <td className="text-right tabular-nums">
                            {formatCurrency((accountStatement.summary.total_cargos_total || accountStatement.summary.total_cargos) - (accountStatement.summary.total_abonos_total || accountStatement.summary.total_abonos))}
                          </td>
                          <td className={`text-right tabular-nums ${(accountStatement.summary.saldo_total || accountStatement.summary.saldo_actual) > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                            {formatCurrency(accountStatement.summary.saldo_total || accountStatement.summary.saldo_actual)}
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>
            </>
          ) : (
            <div className="empty-state py-16 card-industrial mt-6">
              <div className="empty-state-icon bg-slate-100">
                <FileText size={32} className="text-slate-400" />
              </div>
              <h3>Sin movimientos</h3>
              <p>No hay movimientos para este lote en el año seleccionado.</p>
            </div>
          )}
        </TabsContent>

        {/* Consumo de Agua Tab */}
        <TabsContent value="consumo-agua">
          {waterMeter ? (
            <>
              {/* Water Meter Info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 mt-6">
                <div className="card-industrial">
                  <span className="label-overline">Medidor</span>
                  <p className="text-2xl font-black tracking-tight mt-2 font-mono">{waterMeter.numero_medidor}</p>
                  <p className="text-sm text-slate-500 mt-1">{waterMeter.ubicacion || 'Sin ubicación especificada'}</p>
                </div>
                <div className="card-industrial">
                  <span className="label-overline">Lecturas del Año</span>
                  <p className="text-2xl font-black tracking-tight mt-2">{waterReadings.length}</p>
                  <p className="text-sm text-slate-500 mt-1">registros en {selectedYear}</p>
                </div>
                <div className="card-industrial">
                  <span className="label-overline">Total del Año</span>
                  <p className="text-2xl font-black tracking-tight mt-2 tabular-nums">
                    {formatCurrency(waterReadings.reduce((sum, r) => sum + (r.monto_total || 0), 0))}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    {waterReadings.filter(r => r.status === 'Pagada').length} de {waterReadings.length} pagadas
                  </p>
                </div>
              </div>

              {/* Pending Alert */}
              {waterReadings.filter(r => r.status === 'Pendiente').length > 0 && (
                <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded">
                  <div className="flex items-center gap-2 text-amber-800">
                    <AlertCircle size={18} />
                    <span className="font-medium">
                      Tienes {waterReadings.filter(r => r.status === 'Pendiente').length} recibo(s) de agua pendiente(s) de pago por un total de {formatCurrency(waterReadings.filter(r => r.status === 'Pendiente').reduce((sum, r) => sum + (r.monto_total || 0), 0))}
                    </span>
                  </div>
                </div>
              )}

              {/* Readings Table */}
              <div className="card-industrial overflow-hidden p-0">
                <div className="px-6 py-4 border-b border-slate-200">
                  <h3 className="font-bold text-lg">Lecturas de Agua {selectedYear}</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="table-blueprint" data-testid="my-water-table">
                    <thead>
                      <tr>
                        <th>Fecha</th>
                        <th className="text-right">Lectura Anterior</th>
                        <th className="text-right">Lectura Actual</th>
                        <th className="text-right">Consumo (m³)</th>
                        <th className="text-right">Base</th>
                        <th className="text-right">IVA</th>
                        <th className="text-right">Total</th>
                        <th>Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {waterReadings.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="text-center py-8 text-slate-500">
                            No hay lecturas registradas para este año
                          </td>
                        </tr>
                      ) : (
                        waterReadings.map((reading, index) => (
                          <tr key={reading.id || index}>
                            <td className="font-mono text-sm">
                              {new Date(reading.fecha_lectura).toLocaleDateString('es-MX')}
                            </td>
                            <td className="text-right tabular-nums">{reading.lectura_anterior ?? '-'}</td>
                            <td className="text-right tabular-nums font-medium">{reading.lectura_actual}</td>
                            <td className="text-right tabular-nums">
                              {reading.lectura_anterior != null ? (reading.lectura_actual - reading.lectura_anterior) : '-'}
                            </td>
                            <td className="text-right tabular-nums">{formatCurrency(reading.monto_base || 0)}</td>
                            <td className="text-right tabular-nums text-slate-500">{formatCurrency(reading.monto_iva || 0)}</td>
                            <td className="text-right tabular-nums font-medium">{formatCurrency(reading.monto_total || 0)}</td>
                            <td>
                              <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded ${
                                reading.status === 'Pagada' 
                                  ? 'bg-emerald-100 text-emerald-800' 
                                  : 'bg-amber-100 text-amber-800'
                              }`}>
                                {reading.status === 'Pagada' ? (
                                  <CheckCircle size={12} />
                                ) : (
                                  <Clock size={12} />
                                )}
                                {reading.status}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                    {waterReadings.length > 0 && (
                      <tfoot>
                        <tr className="bg-slate-50 font-bold">
                          <td colSpan={4}>TOTAL {selectedYear}</td>
                          <td className="text-right tabular-nums">
                            {formatCurrency(waterReadings.reduce((sum, r) => sum + (r.monto_base || 0), 0))}
                          </td>
                          <td className="text-right tabular-nums">
                            {formatCurrency(waterReadings.reduce((sum, r) => sum + (r.monto_iva || 0), 0))}
                          </td>
                          <td className="text-right tabular-nums">
                            {formatCurrency(waterReadings.reduce((sum, r) => sum + (r.monto_total || 0), 0))}
                          </td>
                          <td></td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>
            </>
          ) : (
            <div className="empty-state py-16 card-industrial mt-6">
              <div className="empty-state-icon bg-blue-100">
                <Droplets size={32} className="text-blue-400" />
              </div>
              <h3>Sin medidor de agua</h3>
              <p>No hay un medidor de agua asignado a este lote.</p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MiCuenta;
